import React, { useEffect, useState } from 'react';
import styles from '../Styles/Campaigns.module.css';
import { previewPlaybookCampaign, getAllEvents } from '../../../Services/arndAPI';
import { CHANNEL_LABEL } from './placeholders';

// SCRUM-449: render a playbook campaign against a real event's audience.

export default function PreviewPanel({ campaign }) {
  const [events, setEvents] = useState(null);
  const [eventId, setEventId] = useState('');
  const [sample, setSample] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // The endpoint needs `order`; without it the list never loads.
    getAllEvents({ order: 'desc' })
      .then((list) => {
        if (cancelled) return;
        const now = Date.now();
        const time = (e) => {
          const t = new Date(e.startsAt ?? e.date ?? 0).getTime();
          return Number.isNaN(t) ? 0 : t;
        };
        // SCRUM-449: GET /admin/events returns rows shaped
        // `[{ eventData: { id, name, startsAt, expiresAt, … } }]` — unwrap
        // before sorting/labelling or the dropdown lists blank entries.
        // Every event, newest first — a closed one can still be previewed.
        const all = (Array.isArray(list) ? list : [])
          .map((row) => row?.eventData ?? row)
          .map((e) => ({
            ...e,
            closed: !!e.expiresAt && new Date(e.expiresAt).getTime() <= now,
          }));
        setEvents(all.sort((a, b) => time(b) - time(a)));
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, []);

  const run = async (id) => {
    setEventId(id);
    setSample(null);
    setError('');
    if (!id) return;
    setLoading(true);
    try {
      const res = await previewPlaybookCampaign(id, campaign);
      setSample(Array.isArray(res) ? res : res?.sample ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.preview}>
      <label className={styles.inlineLabel}>
        Preview event
        <select aria-label="Preview event" value={eventId} onChange={(e) => run(e.target.value)} disabled={!events}>
          <option value="">{events ? 'Choose an event…' : 'Loading events…'}</option>
          {(events ?? []).map((e) => (
            <option key={e.id} value={e.id}>{e.closed ? `${e.name} (closed)` : e.name}</option>
          ))}
        </select>
      </label>
      {loading && <div className={styles.muted}>Rendering…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {sample && sample.length === 0 && <div className={styles.muted}>Nobody in this audience on that event.</div>}
      {sample && sample.length > 0 && (
        <div className={styles.sampleList}>
          {campaign.channels.map((ch) => {
            const rows = sample.filter((s) => s.channel === ch);
            if (!rows.length) return null;
            return (
              <div key={ch} className={styles.sampleGroup}>
                <div className={styles.rowLabel}>{CHANNEL_LABEL[ch]}</div>
                {rows.map((s, i) => (
                  <div key={i} className={styles.sample}>
                    <div className={styles.muted}>{s.parent} · {s.label}</div>
                    <div className={styles.sampleBody}>{s.body}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
